import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import {
  Button,
  CircularProgress,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
  Theme,
} from '@material-ui/core';
import InfiniteScroll from 'react-infinite-scroller';

import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { baseTheme } from '../../theme';
import { GroupToolBar, GroupMemberList, GroupModal, IGroupFormData } from '../../components/group-management';
import { loadGroups, deleteGroupAction, updateGroupAction, IGroupListConfig, clearGroups } from '../../store';
import GroupListEmpty from '../../components/group-management/group-list-empty';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingTop: theme.spacing(1),
      paddingRight: theme.spacing(2),
      paddingBottom: theme.spacing(2),
      paddingLeft: theme.spacing(2),
    },
    pageHeading: {
      fontSize: '1.6em',
      color: theme.palette.primary.main,
      fontWeight: 'normal',
      padding: theme.spacing(0),
      marginTop: theme.spacing(1),
      marginRight: theme.spacing(0),
      marginBottom: theme.spacing(1),
      marginLeft: theme.spacing(0),
    },
    scrollItem: {
      width: '100%',
    },
    loadSpinner: {
      padding: theme.spacing(1),
      margin: '0 auto',
    },
    staticButtonColor: {
      color: theme.palette.text.primary,

      '&:hover': {
        color: theme.palette.text.secondary,
      },
    },
    publicColor: {
      color: theme.palette.primary.main,
    },
    privateColor: {
      color: theme.palette.secondary.main,
    },
    staticColor: {
      color: theme.palette.text.primary,
    },
    closedGroupMenuItem: {
      '&:hover': {
        backgroundColor: theme.palette.secondary.main,
      },
    },
    publicGroupMenuItem: {
      '&:hover': {
      },
    },
  }),
);

export default function GroupManagementPage() {
  const classes = useStyles(baseTheme);
  const dispatch = useDispatch();
  const currUser = useSelector((state: any) => state.auth.currUser);
  const listConfig: IGroupListConfig = useSelector((state: any) => state.groups.listConfig);
  const groupsList = useSelector((state: any) => state.groups.list);
  const groupsFetched = useSelector((state: any) => state.groups.fetched);
  const moreGroups = useSelector((state: any) => state.groups.moreGroups);
  const groupsLoading = useSelector((state: any) => state.groups.loading);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [activeGroup, setActiveGroup] = useState<null | IGroupFormData>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const open = Boolean(anchorEl);
  const [initPageLoad, setInitPageLoad] = useState<boolean>(true);
  const [deleteConfirmation, setDeleteConfirmation] = useState<boolean>(false);

  useEffect(() => {
    if (initPageLoad) {
      clearGroups(dispatch);
      setInitPageLoad(false);
    }
    if (currUser && !groupsFetched && listConfig) {
      loadGroups(currUser.uid, listConfig, dispatch);
    }
  }, [currUser, groupsFetched, listConfig, initPageLoad, dispatch]);

  const loadMoreGroups = () => {
    loadGroups(currUser.uid, listConfig, dispatch);
  };

  const handleIconClick = (group: any) => (event: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setActiveGroup(group);
  };

  const handleClose = (event: any) => {
    event.stopPropagation();
    setAnchorEl(null);
    setActiveGroup(null);
    setDeleteConfirmation(false);
  };

  const handleEdit = (event: any) => {
    event.stopPropagation();
    setAnchorEl(null);
    setIsOpen(true);
  };

  const handleCallBack = (groupData: IGroupFormData | null) => {
    if (groupData) {
      updateGroupAction(groupData, dispatch);
    }

    setIsOpen(false);
  };

  const confirmDelete = () => {
    setDeleteConfirmation(true);
  };

  const handleDelete = (event: any) => {
    event.stopPropagation();
    deleteGroupAction(activeGroup?.id, dispatch);
    setAnchorEl(null);
    setActiveGroup(null);
    setDeleteConfirmation(false);
  };

  const renderList = (groupsList: any[]) => groupsList.map((group: any) => (
    <GroupMemberList key={group.id} group={group} handleIconClick={handleIconClick} />
  ));

  const renderModal = () => (<GroupModal
    open={isOpen}
    editing={true}
    handleCallBack={handleCallBack}
    groupType={activeGroup?.type || 'Closed'}
    propFormValues={activeGroup}
  />);

  const colorSelector = activeGroup?.type === 'Public'
    ? classes.publicColor
    : activeGroup?.type === 'Closed'
      ? classes.privateColor
      : classes.staticColor;
  const menuItemClass = activeGroup?.type === 'Closed'
      ? classes.closedGroupMenuItem
      : classes.publicGroupMenuItem;

  return (
    <Grid item container direction="column" className={classes.root} xs={12}>
      <Grid item container xs={12}>
        <GroupToolBar />
      </Grid>

      <Grid item container spacing={1} xs={12}>
        {
          groupsList.length !== 0
            ? (
              <InfiniteScroll
                pageStart={0}
                loadMore={loadMoreGroups}
                hasMore={!groupsLoading && moreGroups}
                initialLoad={false}
                className={classes.scrollItem}
              >
                {renderList(groupsList)}
              </InfiniteScroll>)
            : <GroupListEmpty />
        }
      </Grid>

      <Grid item container spacing={1} xs={12}>
        {
          groupsLoading && <CircularProgress className={classes.loadSpinner} />
        }
      </Grid>

      <Menu
        id="group-actions-menu"
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={handleClose}
      >
        <MenuItem
          key="edit-group"
          onClick={handleEdit}
          className={menuItemClass}
        >
          Edit Group
        </MenuItem>
        <MenuItem
            key="delete-group"
            onClick={confirmDelete}
            className={menuItemClass}
        >
          Delete Group
        </MenuItem>
      </Menu>

      {activeGroup ? renderModal() : null}

      <Dialog
        open={deleteConfirmation}
        onClose={handleClose}
      >
        <DialogTitle id="alert-delete-dialog">{`Delete ${activeGroup?.name}?`}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you would like to delete {activeGroup?.name}?
          </DialogContentText>
          <DialogActions>
            <Button onClick={handleClose} className={classes.staticButtonColor}>Disagree</Button>
            <Button onClick={handleDelete} className={colorSelector}>Agree</Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </Grid>
  );
}
